import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'expo-router';
import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { useNotifications } from '@/src/context/NotificationContext';
import { NotificationsScreen } from '@/src/presentation/screens/NotificationsScreen';
import { AppNotification } from '@/src/domain/models/Notification';
import {
  getNotificationsUseCase,
  markNotificationReadUseCase,
  markAllNotificationsReadUseCase,
} from '@/src/core/dependencies';
import { logger } from '@/src/core/utils';

export default function NotificationsContainer() {
  const { user } = useAuth();
  const { colors } = useTheme();
  const { refreshUnreadCount } = useNotifications();
  const router = useRouter();

  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [offset, setOffset] = useState(0);
  const PAGE_SIZE = 20;

  const loadNotifications = useCallback(async (reset = false) => {
    if (!user) return;

    try {
      const newOffset = reset ? 0 : offset;
      const data = await getNotificationsUseCase.execute(user.id, PAGE_SIZE, newOffset);

      if (reset) {
        setNotifications(data);
        setOffset(PAGE_SIZE);
      } else {
        setNotifications((prev) => [...prev, ...data]);
        setOffset((prev) => prev + PAGE_SIZE);
      }
    } catch (error) {
      logger.error('Notifications', 'Failed to load notifications', { error });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user, offset]);

  useEffect(() => {
    loadNotifications(true);
  }, [user]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadNotifications(true);
  };

  const handleLoadMore = () => {
    if (!loading && notifications.length >= offset) {
      loadNotifications(false);
    }
  };

  const handleNotificationPress = async (notification: AppNotification) => {
    // Mark as read
    if (!notification.read) {
      try {
        await markNotificationReadUseCase.execute(notification.id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
        );
        refreshUnreadCount();
      } catch (error) {
        logger.error('Notifications', 'Failed to mark notification as read', { error });
      }
    }

    // Navigate based on type
    const { type, data } = notification;
    switch (type) {
      case 'tournament_started':
      case 'tournament_completed':
      case 'match_result':
        if (data.tournamentId) router.push(`/bracket/${data.tournamentId}` as any);
        break;
      case 'tournament_invitation':
        if (data.tournamentId) router.push(`/tournament/${data.tournamentId}` as any);
        break;
      case 'friend_request':
        router.push('/friend-requests' as any);
        break;
      case 'challenge_received':
        router.push('/challenges' as any);
        break;
      case 'league_invitation':
        if (data.leagueId) router.push(`/league/${data.leagueId}` as any);
        break;
    }
  };

  const handleMarkAllRead = async () => {
    if (!user) return;

    try {
      await markAllNotificationsReadUseCase.execute(user.id);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      refreshUnreadCount();
    } catch (error) {
      logger.error('Notifications', 'Failed to mark all as read', { error });
    }
  };

  return (
    <NotificationsScreen
      notifications={notifications}
      loading={loading}
      onNotificationPress={handleNotificationPress}
      onMarkAllRead={handleMarkAllRead}
      onRefresh={handleRefresh}
      refreshing={refreshing}
      onLoadMore={handleLoadMore}
      colors={colors}
    />
  );
}
